import { useEffect, useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootState } from 'redux/store';
import { fetchAnnouncementDetails } from 'redux/slices/announcements/announcementDetailSlice';
import { getStaffList } from 'redux/slices/staff/staffSlice';
import { getDateAndTime } from 'utils/helpers';
import AssistanceFooter from 'components/Common/AssistanceFooter';
import Button from 'components/Common/Button';
import CardWrapper from 'components/Common/CardWrapper';
import MuiLoader from 'components/Common/Loader';
import Routes from 'constants/routes';
import { selectSelectedParishId, selectEffectiveStaffRole } from 'redux/selectors/parishContext';
import { hasAtLeastRole } from 'api/v1Helpers';

function AnnouncementDetails() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id } = useParams();
    const parishId = useSelector(selectSelectedParishId);
    const effectiveRole = useSelector(selectEffectiveStaffRole);
    const canManageAnnouncements = hasAtLeastRole(effectiveRole, 'MERCHANT_ADMIN');
    const { details, loading } = useSelector((state: RootState) => state.announcement.announcementDetails);
    const { items: staffItems, loadedParishId: staffLoadedParishId } = useSelector((state: RootState) => state.staff.staffList);
    const { title, message, createDate, createdById } = details || {};

    useEffect(() => {
        if (id && parishId) {
            dispatch(fetchAnnouncementDetails({ parishId, id }));
        }
    }, [id, parishId, dispatch]);

    // Resolve the creator's name from the staff roster already used by the Staff tab, rather
    // than showing the raw user id — fetch it once per parish if it isn't already loaded.
    useEffect(() => {
        if (parishId && staffLoadedParishId !== parishId) {
            dispatch(getStaffList({ parishId }));
        }
    }, [parishId, staffLoadedParishId, dispatch]);

    const createdByName = useMemo(() => {
        if (!createdById) return null;
        const match = staffItems.find((s) => s.user.id === createdById);
        if (!match) return null;
        return [match.user.firstName, match.user.lastName].filter(Boolean).join(' ') || match.user.email;
    }, [staffItems, createdById]);

    const handleResendAnnouncement = () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- pre-existing short-circuit-as-conditional idiom
        id && navigate(Routes.announcements.edit.replace(':id', id));
    };

    return (
        <CardWrapper>
            <MuiLoader loading={loading} absolute />
            <Stack justifyContent="center" alignItems="center">
                <Typography
                    sx={{
                        fontSize: { xs: 18, md: 26 },
                        mb: 2,
                        textAlign: 'left',
                        width: { xs: '75%', xl: '50%' },
                        color: '#220E40',
                        fontWeight: 400,
                    }}
                    variant="h1"
                >
                    {title}
                </Typography>
                <Typography
                    sx={{ mt: 2, mb: 4, textAlign: 'left', width: { xs: '75%', xl: '50%' }, fontSize: { xs: 12, md: 18 } }}
                    variant="body1"
                >
                    {message}
                </Typography>

                <Typography
                    sx={{ mt: 1, mb: 3, textAlign: 'left', width: { xs: '75%', xl: '50%' }, fontSize: { xs: 9, md: 14 } }}
                    variant="body1"
                >
                    Date sent: {createDate && getDateAndTime(createDate)}
                    <br />
                    <span>Created by: {createdByName || createdById || '—'}</span>
                </Typography>

                {canManageAnnouncements && (
                    <Button onClick={handleResendAnnouncement} sx={{ width: { xs: '100%', sm: '60%', xl: '50%' } }}>
                        Resend Announcement
                    </Button>
                )}
            </Stack>
            <AssistanceFooter />
        </CardWrapper>
    );
}

export default AnnouncementDetails;
